import type { Translate } from 'next-translate'
import type { ParsedUrlQueryInput } from 'querystring'
import type { UrlObject } from 'url'
import { EAppraisalHandoffTarget } from '@/types/gql/graphql'
import type { AppraisalHandoff } from './useAppraisalFlow'

export type VehicleHandoff = NonNullable<AppraisalHandoff['vehicle']>

/** Query parameters the vehicle forms read (see parseCarFormQuery / VehicleDealBooking). */
export const VEHICLE_QUERY_KEYS = {
  amount: 'betrag',
  make: 'marke',
  model: 'modell',
  registrationYear: 'zulassungsjahr',
  mileageKm: 'km',
  horsePower: 'ps',
  fuel: 'kraftstoff',
} as const

/**
 * Cars go into the autopfand form: straight to step 3 (registration year)
 * when make and model are known, to step 2 with a known make, else step 1.
 * Motorcycles go to /motorrad with every known detail prefilled.
 */
export function buildVehicleHandoffHref(
  target: EAppraisalHandoffTarget,
  vehicle: VehicleHandoff | null,
  loanAmount: number,
): UrlObject {
  const { amount, make, model, registrationYear, mileageKm, horsePower, fuel } =
    VEHICLE_QUERY_KEYS
  if (target === EAppraisalHandoffTarget.Motorcycle) {
    return {
      pathname: '/motorrad',
      query: compact({
        [amount]: loanAmount,
        [make]: vehicle?.make,
        [model]: vehicle?.model,
        [registrationYear]: vehicle?.registrationYear,
        [mileageKm]: vehicle?.mileageKm,
        [horsePower]: vehicle?.horsePower,
        [fuel]: vehicle?.fuel,
      }),
    }
  }
  if (vehicle?.make && vehicle.model) {
    return {
      pathname: '/autopfand-formular/zulassungsjahr',
      query: {
        [amount]: loanAmount,
        [make]: vehicle.make,
        [model]: vehicle.model,
      },
    }
  }
  if (vehicle?.make) {
    return {
      pathname: '/autopfand-formular/modell',
      query: { [amount]: loanAmount, [make]: vehicle.make },
    }
  }
  return { pathname: '/autopfand-formular', query: { [amount]: loanAmount } }
}

/** The detected details as label/value rows (only what is known). */
export function vehicleDetailRows(
  vehicle: VehicleHandoff | null,
  t: Translate,
): { label: string; value: string }[] {
  if (!vehicle) return []
  const rows: { label: string; value: string }[] = []
  if (vehicle.make)
    rows.push({ label: t('common:car.make'), value: vehicle.make })
  if (vehicle.model)
    rows.push({ label: t('common:car.model'), value: vehicle.model })
  if (vehicle.registrationYear)
    rows.push({
      label: t('common:car.regdate'),
      value: String(vehicle.registrationYear),
    })
  if (vehicle.mileageKm)
    rows.push({
      label: t('common:car.odometer'),
      value: `${vehicle.mileageKm.toLocaleString('de-AT')} km`,
    })
  if (vehicle.horsePower)
    rows.push({
      label: t('wertCheck:vehicle_power'),
      value: `${vehicle.horsePower} PS`,
    })
  if (vehicle.fuel)
    rows.push({
      label: t('common:car.fuel'),
      value: t(`wertCheck:fuel_${vehicle.fuel}`),
    })
  return rows
}

function compact(
  query: Record<string, string | number | null | undefined>,
): ParsedUrlQueryInput {
  return Object.fromEntries(
    Object.entries(query).filter(
      ([, v]) => v !== undefined && v !== null && v !== '',
    ),
  )
}
